/**
 * PMD Sticky ATC — Subscribe / Buy now (Intelligems variant B).
 *
 * Drives snippets/pmd-sticky-atc-subscribe.liquid. It owns no pricing logic:
 * every number is read back from the buy box that
 * scripts/sections/variant-button.js renders, and both buttons act by checking
 * the buy box's own purchase-type radio and then clicking <buy-button>. That
 * keeps the sticky bar correct for any pack, variant, discount type or
 * sold-out state without a second source of truth.
 *
 * Author: PM Digital Design.
 */
class PmdStickyAtcSubscribe extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form[action="/cart/add"]')
    if (!this.form) return
    this.stickyBar = this.closest('sticky-add-to-cart')

    // Runs for BOTH variants, deliberately: the bar has to appear at the same
    // moment in A and in B, otherwise the test measures the trigger change on
    // top of the layout change and neither result is attributable.
    this.watchBuyBox()

    // Nothing below is gated on the Intelligems flag, on purpose. CSS alone
    // decides whether variant B's markup is visible, and CSS applies whenever
    // the attribute lands. Gating the script too would mean racing Intelligems:
    // this file is deferred, so an attribute set even slightly later would find
    // the element already given up, and the bar would stay on the control CTA
    // for the whole session. What follows only ever writes into this element's
    // own hidden children, so it is inert for the control either way.
    this.subscribeButton = this.querySelector('[data-sas-subscribe]')
    this.oneTimeButton = this.querySelector('[data-sas-onetime]')
    this.subscribeLabel = this.querySelector('[data-sas-sub-label]')
    this.subscribeSeparator = this.querySelector('[data-sas-sub-separator]')
    this.subscribePrice = this.querySelector('[data-sas-sub-price]')
    this.oneTimePrice = this.querySelector('[data-sas-onetime-price]')

    this.sync = this.sync.bind(this)
    this.onClick = this.onClick.bind(this)

    this.sync()
    this.addEventListener('click', this.onClick)

    // variant-button.js re-renders the whole selling-plan fieldset on every
    // pack change, so observe the container rather than the nodes inside it.
    const buyBox = this.form.querySelector('.quick-view__product-selling-plans')
    if (buyBox) {
      this.observer = new MutationObserver(this.sync)
      this.observer.observe(buyBox, {
        subtree: true,
        childList: true,
        characterData: true,
      })
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect()
    this.buyBoxObserver?.disconnect()
    clearTimeout(this.unpinTimer)
    this.removeEventListener('click', this.onClick)
  }

  /**
   * Pins the bar whenever the buy box is off screen, replacing the theme's
   * scroll handler, which compares scrollY against the bar's own offsetTop —
   * an offset that stops meaning anything the moment the bar is position:fixed.
   *
   * `.pdp-buttons-wrapper` is the anchor rather than the bar itself: the bar
   * leaves the flow when it pins, so observing it would report it back in view
   * and oscillate. The wrapper stays put, and its height is frozen up front so
   * the bar leaving the flow cannot move it either.
   *
   * The theme keeps toggling its own `.is-fixed`; the accompanying CSS makes
   * that class inert on its own, so the two never fight over a shared class.
   */
  watchBuyBox() {
    const anchor = this.form.querySelector('.pdp-buttons-wrapper')
    if (!anchor || !this.stickyBar || !('IntersectionObserver' in window)) return

    const barHeight = this.stickyBar.offsetHeight
    if (barHeight) anchor.style.minHeight = `${barHeight}px`

    this.buyBoxObserver = new IntersectionObserver(
      ([entry]) => this.setPinned(!entry.isIntersecting),
      { threshold: 0 }
    )
    this.buyBoxObserver.observe(anchor)
  }

  /**
   * Pins and unpins the bar, holding the fixed presentation across the exit so
   * the bar can slide away instead of vanishing. `pmd-sticky-pinned` carries
   * that presentation for both directions; `pmd-sticky-on` and
   * `pmd-sticky-leaving` carry the two halves of the motion.
   * @param {boolean} pinned
   */
  setPinned(pinned) {
    const bar = this.stickyBar
    if (pinned === bar.classList.contains('pmd-sticky-on')) return

    clearTimeout(this.unpinTimer)

    if (pinned) {
      // Scrolling back down mid-exit: cancel the departure rather than let it
      // finish and unpin a bar that should be on screen.
      bar.classList.remove('pmd-sticky-leaving')
      bar.classList.add('pmd-sticky-pinned', 'pmd-sticky-on')
      return
    }

    if (!bar.classList.contains('pmd-sticky-pinned')) return

    bar.classList.remove('pmd-sticky-on')

    // Reduced motion means the exit animation is `none`, so animationend never
    // fires and waiting on it would strand the bar fixed and invisible.
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
    if (reducedMotion) {
      bar.classList.remove('pmd-sticky-pinned')
      return
    }

    bar.classList.add('pmd-sticky-leaving')
    // Timer rather than animationend: the event also does not fire when the tab
    // is hidden or the animation is interrupted, and a stuck bar covers the page.
    this.unpinTimer = setTimeout(() => {
      bar.classList.remove('pmd-sticky-pinned', 'pmd-sticky-leaving')
    }, 500)
  }

  /**
   * The buy box prints the discounted price last, after an optional struck
   * compare-at price, in both the discounted and the undiscounted markup.
   * @param {string} selector
   * @returns {string|null}
   */
  readPrice(selector) {
    const holder = this.form.querySelector(selector)
    if (!holder) return null
    const spans = holder.querySelectorAll('span')
    const text = (spans.length ? spans[spans.length - 1] : holder).textContent
    return text.trim() || null
  }

  /**
   * Turns the buy box's "Save 10%" / "Save $5" badge into the CTA's
   * "Subscribe for 10% Off" / "Subscribe for $5 Off" wording. Without a badge
   * there is no saving to advertise, so the CTA falls back to the buy box copy.
   * @returns {string}
   */
  readSubscribeLabel() {
    const badge = this.form.querySelector(
      '.subscribe-and-save:not(.prepaid) span'
    )
    const saving = badge?.textContent.trim().replace(/^Save\s+/i, '')
    return saving ? `Subscribe for ${saving} Off` : 'Subscribe & Save'
  }

  /** Mirrors the buy box's prices, discount copy and availability. */
  sync() {
    const subscribePrice = this.readPrice(
      '.subscribe-and-save-price:not(.prepaid)'
    )
    const oneTimePrice = this.readPrice('.one-time-purchase-price')

    if (subscribePrice) this.subscribePrice.textContent = subscribePrice
    if (oneTimePrice) this.oneTimePrice.textContent = oneTimePrice
    this.subscribeLabel.textContent = this.readSubscribeLabel()
    this.subscribeSeparator.hidden = false
    this.subscribePrice.hidden = false

    // The one-time radio is disabled on subscription-only products. The bar is
    // not rendered for those, but honour it rather than offering a dead link.
    const oneTimeRadio = this.form.querySelector('#oneTime')
    this.oneTimeButton.hidden = !oneTimeRadio || oneTimeRadio.disabled

    // Mirror, never rewrite: the label/price nodes stay in the DOM so a variant
    // going back in stock restores the CTA without re-rendering the button.
    const controlCta = this.form.querySelector('.product__cta')
    const soldOut =
      !!controlCta &&
      (controlCta.disabled || controlCta.classList.contains('sold-out'))
    this.subscribeButton.disabled = soldOut
    this.oneTimeButton.disabled = soldOut
    this.subscribeSeparator.hidden = soldOut
    this.subscribePrice.hidden = soldOut
    if (soldOut) this.subscribeLabel.textContent = 'Sold Out'
  }

  /**
   * Selects the matching purchase type in the buy box, then adds to cart.
   * The radio click bubbles to <variant-button>, which is what sets the
   * `selling-plan` attribute <buy-button> reads — so the order matters.
   * @param {MouseEvent} event
   */
  onClick(event) {
    const wantsSubscription = !!event.target.closest('[data-sas-subscribe]')
    const wantsOneTime = !!event.target.closest('[data-sas-onetime]')
    if (!wantsSubscription && !wantsOneTime) return

    event.preventDefault()

    const radio = this.form.querySelector(
      wantsSubscription ? '#stay-subscribe' : '#oneTime'
    )
    if (radio && !radio.checked) radio.click()

    this.form.querySelector('buy-button')?.click()
  }
}

customElements.define('pmd-sticky-atc-subscribe', PmdStickyAtcSubscribe)
